import NetEqualisationInterestPaymentTable from './NetEqualisationInterestPaymentTable';
import EqualisationInterestCalculationsItem from './EqualisationInterestCalculationsItem';
import { useParams } from 'react-router-dom';
import { useLayoutContext } from 'contexts/LayoutContext';
import api from 'lib/trpcClient';
import EqualisationFooter from '../EqualisationFooter';
import { EqualisationSection } from 'further-types/lp/equalisation';
import { useEqualisationSection } from '../EqualisationContext';
import { CurrencyCode } from 'further-types/lp/vehicle';
import ButtonGroup from 'components/ButtonGroup';
import Button from 'components/Button';
import Spacing from 'components/Spacing';
import { useState } from 'react';
import PageSection from 'components/PageSection';
import { useFormContext } from 'react-hook-form';
import { EqualisationInterestFormValues } from './FormWrapper';
import { CalendarDay } from 'further-ui/utils';
import { round, sumBy } from 'lodash';
import useHandleTrpcMutation from 'hooks/ui/useHandleTrpcMutation';
import { getQueryKey } from '@trpc/react-query';

interface EqualisationInterestContentProps {
  baseCurrency: CurrencyCode;
  calculationBreakdown: Array<{
    id: string;
    name: string;
    ledgerDate: CalendarDay;
    totalAmountLcy: number;
    newInvestorBreakdown: Array<{
      investorName: string;
      commitmentId: string;
      investments: number;
      fees: number;
      expenses: number;
      workingCapital: number;
      returnOfCapital: number;
      netCallOrDistribution: number;
      callOverpaidOrUnderpaid: number;
      runningTotalCallOverpaidOrUnderpaid: number;
      equalisationInterestTotalLcy: number;
      equalisationInterestPercentage: number;
    }>;
  }>;
}

const EqualisationInterestContent: React.FC<
  EqualisationInterestContentProps
> = ({ baseCurrency, calculationBreakdown }) => {
  const { id } = useParams<{ id?: string }>();
  const { selectedVehicleId } = useLayoutContext();
  const { getValues, watch } = useFormContext<EqualisationInterestFormValues>();
  const equalisationInterestPaymentBreakdown = watch(
    'equalisationInterestPaymentBreakdown',
  );

  const { previousSection, nextSection, goToNextSection, goToPreviousSection } =
    useEqualisationSection();

  const watchedBreakdown = watch('equalisationInterestPaymentBreakdown');
  const totalEqualisationInterestLcy =
    round(sumBy(watchedBreakdown, 'equalisationInterestTotalLcy') ?? 0, 2) === 0
      ? 0
      : round(sumBy(watchedBreakdown, 'equalisationInterestTotalLcy') ?? 0, 2);

  const equalisationActionAllocationsQueryKey = getQueryKey(
    api.equalisations.getInterest,
    {
      equalisationId: id ?? '',
      vehicleId: selectedVehicleId ?? '',
    },
  );

  const updateEqualisation = api.equalisations.update.useMutation(
    useHandleTrpcMutation()({
      invalidations: [equalisationActionAllocationsQueryKey],
    }),
  );

  const [showCalculations, setShowCalculations] = useState(false);

  const handleNext = () => {
    updateEqualisation.mutate(
      {
        equalisationId: id ?? '',
        vehicleId: selectedVehicleId ?? '',
        currentSection: nextSection ?? EqualisationSection.Summary,
        equalisationInterestPaymentBreakdown: getValues(
          'equalisationInterestPaymentBreakdown',
        ),
      },
      {
        onSuccess: () => {
          goToNextSection();
        },
      },
    );
  };

  const handlePrevious = () => {
    updateEqualisation.mutate(
      {
        equalisationId: id ?? '',
        vehicleId: selectedVehicleId ?? '',
        currentSection: previousSection ?? EqualisationSection.Commitments,
        equalisationInterestPaymentBreakdown: getValues(
          'equalisationInterestPaymentBreakdown',
        ),
      },
      {
        onSuccess: () => {
          goToPreviousSection();
        },
      },
    );
  };

  return (
    <Spacing>
      <PageSection
        title="Net equalisation interest payment breakdown"
        withSpacing
      >
        <NetEqualisationInterestPaymentTable
          netEqualisationInterestPaymentBreakdown={
            equalisationInterestPaymentBreakdown
          }
          baseCurrency={baseCurrency}
          totalEqualisationInterestLcy={totalEqualisationInterestLcy}
        />
        <ButtonGroup align="left">
          <Button
            variant="outlined"
            onClick={() => setShowCalculations(!showCalculations)}
          >
            {showCalculations ? 'Hide calculations' : 'Show calculations'}
          </Button>
        </ButtonGroup>
      </PageSection>

      {showCalculations && (
        <PageSection title="Per capital action calculations" showDivider>
          {calculationBreakdown.map((action, index) => (
            <EqualisationInterestCalculationsItem
              key={index}
              action={action}
              baseCurrency={baseCurrency}
              index={index}
              isLastElement={index === calculationBreakdown.length - 1}
            />
          ))}
        </PageSection>
      )}
      <EqualisationFooter
        handleNext={handleNext}
        handlePrevious={handlePrevious}
        disabled={totalEqualisationInterestLcy !== 0}
        loading={updateEqualisation.isPending}
        tooltipDisabledMessage="Please ensure that the total equalisation interest payment breakdown equals 0."
      />
    </Spacing>
  );
};

export default EqualisationInterestContent;
